import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import { useNFCContext } from '../contexts/NFCContext';
import { useTransactionContext } from '../contexts/TransactionContext';
import { formatCurrency, formatDate } from '../utils/formatters';
import { toast } from 'react-toastify';
import './Balance.css';

const NFC_STATES = {
  IDLE: 'idle',
  SCANNING: 'scanning',
  SUCCESS: 'success',
  ERROR: 'error'
};

const Balance = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { startScan, nfcData: contextNfcData } = useNFCContext();
  const { getTransactionsByStudent } = useTransactionContext();

  const [nfcState, setNfcState] = useState(NFC_STATES.IDLE);
  const [studentData, setStudentData] = useState(null);
  const [transactions, setTransactions] = useState([]);

  const handleScanCard = async () => {
    try {
      setNfcState(NFC_STATES.SCANNING);
      
      // Iniciar escaneo NFC
      await startScan();
      
      // Simular delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Usar datos del contexto NFC
      const data = contextNfcData || { id: '123', nombre: 'Estudiante Demo', grado: '10°', saldo: 25000 };
      setStudentData(data);
      
      // Obtener últimas transacciones del estudiante
      const studentTx = getTransactionsByStudent(data.id).slice(0, 5);
      setTransactions(studentTx);
      
      setNfcState(NFC_STATES.SUCCESS);
      
      toast.success(`Información de ${data.nombre} cargada`, {
        position: "top-center",
        autoClose: 2000
      });
    } catch (error) {
      console.error('Error reading NFC:', error);
      setNfcState(NFC_STATES.ERROR);
      toast.error('Error al leer el carnet NFC', {
        position: "top-center"
      });
    }
  };

  const handleNewScan = () => {
    setNfcState(NFC_STATES.IDLE);
    setStudentData(null);
    setTransactions([]);
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'pago':
        return '💳';
      case 'recarga':
        return '💰';
      default:
        return '📝';
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'pago':
        return '#ef4444';
      case 'recarga':
        return '#10b981';
      default:
        return '#6366f1';
    }
  };

  return (
    <div className="balance-container">
      {/* Header */}
      <header className="balance-header">
        <div className="header-content">
          <button onClick={() => navigate('/dashboard')} className="back-btn">
            <span className="back-icon">←</span>
            <span>Volver</span>
          </button>
          <div className="header-title">
            <div className="title-icon">🔍</div>
            <div>
              <h1>Consultar Saldo</h1>
              <p>Información de carnet NFC</p>
            </div>
          </div>
          <div className="header-badge">
            <span className="badge-icon">🏪</span>
            <span>{user?.nombre}</span>
          </div>
        </div>
      </header>

      <div className="balance-content">
        {/* Scanner Panel */}
        <div className="scanner-panel">
          {nfcState === NFC_STATES.IDLE && (
            <div className="scan-area">
              <div className="scan-instruction">
                <div className="instruction-icon">📱</div>
                <h2>Escanear Carnet NFC</h2>
                <p>Acerca el carnet al lector para ver el saldo e información del estudiante</p>
              </div>
              <button onClick={handleScanCard} className="scan-btn">
                <div className="scan-icon-wrapper">
                  <div className="scan-rings">
                    <div className="ring ring-1"></div>
                    <div className="ring ring-2"></div>
                    <div className="ring ring-3"></div>
                  </div>
                  <span className="scan-icon">📱</span>
                </div>
                <span className="scan-text">Iniciar Escaneo</span>
              </button>
            </div>
          )}

          {nfcState === NFC_STATES.SCANNING && (
            <div className="scanning-state">
              <div className="scanning-animation">
                <div className="pulse-rings">
                  <div className="pulse-ring"></div>
                  <div className="pulse-ring"></div>
                  <div className="pulse-ring"></div>
                </div>
                <div className="nfc-icon-animated">📱</div>
              </div>
              <h3>Leyendo carnet...</h3>
              <p>Mantén el carnet cerca del lector</p>
              <div className="progress-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          )}

          {nfcState === NFC_STATES.ERROR && (
            <div className="error-state">
              <div className="error-icon">⚠️</div>
              <h3>Error de lectura</h3>
              <p>No se pudo leer el carnet. Verifica que esté cerca del lector.</p>
              <button onClick={handleScanCard} className="retry-btn">
                Reintentar
              </button>
            </div>
          )}
        </div>

        {/* Info Panel */}
        {nfcState === NFC_STATES.SUCCESS && studentData && (
          <div className="info-panel">
            {/* Student Card */}
            <div className="student-info-card">
              <div className="info-card-header">
                <div className="student-avatar-large">
                  {studentData.nombre.split(' ').map(n => n[0]).join('').substring(0, 2)}
                </div>
                <div className="verified-badge">
                  <span className="badge-icon">✓</span>
                  <span>Verificado</span>
                </div>
              </div>

              <div className="student-info">
                <h2 className="student-name">{studentData.nombre}</h2>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-icon">🎓</span>
                    <div className="info-details">
                      <span className="info-label">Grado</span>
                      <span className="info-value">{studentData.grado}</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <span className="info-icon">🆔</span>
                    <div className="info-details">
                      <span className="info-label">ID</span>
                      <span className="info-value">{studentData.id}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="balance-display">
                <div className="balance-label">
                  <span className="balance-icon">💰</span>
                  <span>Saldo Disponible</span>
                </div>
                <div className="balance-amount">
                  {formatCurrency(studentData.saldo)}
                </div>
              </div>
            </div>

            {/* Recent Transactions */}
            <div className="transactions-section">
              <div className="section-header">
                <h3>
                  <span className="header-icon">📋</span>
                  Últimas Transacciones
                </h3>
              </div>

              {transactions.length === 0 ? (
                <div className="no-transactions">
                  <div className="no-tx-icon">📭</div>
                  <p>No hay transacciones recientes</p>
                </div>
              ) : (
                <div className="transactions-list">
                  {transactions.map((tx, index) => (
                    <div key={index} className="transaction-item">
                      <div className="tx-icon" style={{ background: getTransactionColor(tx.tipo) }}>
                        {getTransactionIcon(tx.tipo)}
                      </div>
                      <div className="tx-info">
                        <div className="tx-type">
                          {tx.tipo === 'pago' ? 'Pago' : 'Recarga'}
                        </div>
                        <div className="tx-date">
                          {formatDate(tx.fecha)}
                        </div>
                      </div>
                      <div className="tx-amount" style={{ color: getTransactionColor(tx.tipo) }}>
                        {tx.tipo === 'pago' ? '-' : '+'}{formatCurrency(tx.monto)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="actions-section">
              <button onClick={handleNewScan} className="new-scan-btn">
                <span className="btn-icon">🔄</span>
                <span>Escanear Otro Carnet</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Balance;
